<app-menu>
  <div class="tg-shell" [attr.data-theme]="themeMode">
    <div class="tg-wrapper">
      <header class="tg-toolbar">
        <div class="tg-title-block">
          <div class="tg-eyebrow">Admin Tools / Technogym Integration</div>
          <div class="tg-title-row">
            <h1>Technogym SFTP Browser</h1>
            <span class="tg-readonly-pill">
              <i class="fa-solid fa-lock"></i>
              Read only
            </span>
          </div>
          <p>Browse Technogym exchange folders, preview text and CSV files, and download originals.</p>
        </div>

        <div class="tg-toolbar-actions">
          <button type="button" class="tg-button tg-button--ghost" (click)="toggleTheme()">
            <i class="fa-solid" [ngClass]="themeMode === 'dark' ? 'fa-sun' : 'fa-moon'"></i>
            <span>{{ themeMode === 'dark' ? 'Light' : 'Dark' }}</span>
          </button>
          <button type="button" class="tg-button tg-button--primary" (click)="refresh()" [disabled]="loading">
            <i class="fa-solid fa-rotate" [class.fa-spin]="loading"></i>
            <span>{{ loading ? 'Loading' : 'Refresh' }}</span>
          </button>
        </div>
      </header>

      <div class="tg-alert tg-alert--error" *ngIf="errorMessage">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <main class="tg-layout">
        <aside class="tg-panel tg-folder-panel">
          <div class="tg-panel-heading">
            <div>
              <div class="tg-eyebrow">Allowlisted folders</div>
              <h2>SFTP directories</h2>
            </div>
            <span class="tg-count-pill">{{ folders.length }}</span>
          </div>

          <nav class="tg-folder-groups" aria-label="Technogym SFTP folders">
            <section class="tg-folder-group">
              <h3>Current exchange</h3>
              <button
                type="button"
                class="tg-folder-button"
                *ngFor="let folder of activeFolders; trackBy: trackByFolder"
                [class.is-active]="folder.id === selectedFolderId"
                (click)="loadFolder(folder.id)"
                [disabled]="loading"
              >
                <span class="tg-folder-icon" [ngClass]="'is-' + folder.direction">
                  <i [class]="directionIcon(folder.direction)"></i>
                </span>
                <span class="tg-folder-copy">
                  <strong>{{ folder.label }}</strong>
                  <small>{{ directionLabel(folder.direction) }}</small>
                </span>
                <i class="fa-solid fa-chevron-right tg-folder-chevron"></i>
              </button>
            </section>

            <section class="tg-folder-group" *ngIf="archiveFolders.length">
              <h3>Archives</h3>
              <button
                type="button"
                class="tg-folder-button"
                *ngFor="let folder of archiveFolders; trackBy: trackByFolder"
                [class.is-active]="folder.id === selectedFolderId"
                (click)="loadFolder(folder.id)"
                [disabled]="loading"
              >
                <span class="tg-folder-icon is-archive">
                  <i class="fa-solid fa-box-archive"></i>
                </span>
                <span class="tg-folder-copy">
                  <strong>{{ folder.label }}</strong>
                  <small>{{ directionLabel(folder.direction) }}</small>
                </span>
                <i class="fa-solid fa-chevron-right tg-folder-chevron"></i>
              </button>
            </section>
          </nav>

          <div class="tg-safety-card">
            <i class="fa-solid fa-shield-halved"></i>
            <div>
              <strong>Technogym file access</strong>
              <span>Browse approved exchange folders with file details, text previews, CSV tables, and original downloads.</span>
            </div>
          </div>
        </aside>

        <section class="tg-content">
          <section class="tg-panel tg-file-panel">
            <header class="tg-file-header">
              <div class="tg-folder-title">
                <div class="tg-folder-title-icon" [ngClass]="'is-' + (selectedFolder?.direction || 'inbound')">
                  <i [class]="directionIcon(selectedFolder?.direction || 'inbound')"></i>
                </div>
                <div>
                  <div class="tg-eyebrow">{{ selectedFolder?.category === 'archive' ? 'Archive' : 'Live directory' }}</div>
                  <h2>{{ selectedFolder?.label || 'Loading folder' }}</h2>
                  <p>{{ selectedFolder?.description }}</p>
                </div>
              </div>

              <div class="tg-file-stats">
                <span><strong>{{ files.length }}</strong> files</span>
                <span><strong>{{ formatBytes(totalBytes) }}</strong> total</span>
                <span *ngIf="generatedAt">Loaded {{ formatDate(generatedAt) }}</span>
              </div>
            </header>

            <div class="tg-directory-bar" *ngIf="selectedFolder">
              <span class="tg-direction-badge" [ngClass]="'is-' + selectedFolder.direction">
                <i [class]="directionIcon(selectedFolder.direction)"></i>
                {{ directionLabel(selectedFolder.direction) }}
              </span>
              <span class="tg-archive-badge" *ngIf="selectedFolder.category === 'archive'">
                <i class="fa-solid fa-box-archive"></i>
                Archive
              </span>
              <code>{{ selectedFolder.path }}</code>
            </div>

            <div class="tg-list-tools">
              <label class="tg-search">
                <i class="fa-solid fa-magnifying-glass"></i>
                <input type="search" [(ngModel)]="searchText" (ngModelChange)="resetFileDisplayLimit()" placeholder="Filter filenames" aria-label="Filter Technogym SFTP filenames">
                <button type="button" *ngIf="searchText" (click)="searchText = ''; resetFileDisplayLimit()" aria-label="Clear filename filter">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </label>
              <span>{{ visibleFiles.length }} of {{ filteredFiles.length }} shown</span>
            </div>

            <div class="tg-loading" *ngIf="loading">
              <i class="fa-solid fa-circle-notch fa-spin"></i>
              <strong>Reading Technogym SFTP…</strong>
              <span>Loading directory contents.</span>
            </div>

            <div class="tg-file-list" *ngIf="!loading && visibleFiles.length">
              <div
                class="tg-file-row"
                *ngFor="let file of visibleFiles; trackBy: trackByFile"
                [class.is-selected]="selectedFile?.filename === file.filename"
                [class.is-disabled]="!!previewingFilename || !!downloadingFilename"
                role="button"
                tabindex="0"
                (click)="preview(file)"
                (keydown.enter)="preview(file)"
                (keydown.space)="preview(file); $event.preventDefault()"
              >
                <span class="tg-file-type">
                  <i [class]="fileIcon(file)"></i>
                </span>
                <span class="tg-file-main">
                  <strong>{{ file.filename }}</strong>
                  <small>{{ formatDate(file.modified_at) }}</small>
                </span>
                <span class="tg-file-meta">
                  <span class="tg-direction-badge tg-direction-badge--small" [ngClass]="'is-' + file.direction">
                    {{ directionLabel(file.direction) }}
                  </span>
                  <span>{{ formatBytes(file.size) }}</span>
                </span>
                <span class="tg-row-actions">
                  <span class="tg-icon-button" [class.is-busy]="previewingFilename === file.filename" title="Preview file">
                    <i class="fa-solid" [ngClass]="previewingFilename === file.filename ? 'fa-circle-notch fa-spin' : 'fa-eye'"></i>
                  </span>
                  <button
                    type="button"
                    class="tg-icon-button"
                    title="Download file"
                    [disabled]="!!previewingFilename || !!downloadingFilename"
                    (click)="download(file, $event)"
                  >
                    <i class="fa-solid" [ngClass]="downloadingFilename === file.filename ? 'fa-circle-notch fa-spin' : 'fa-download'"></i>
                  </button>
                </span>
              </div>
            </div>

            <div class="tg-list-footer" *ngIf="!loading && hasMoreFiles">
              <span>{{ filteredFiles.length - visibleFiles.length }} more files</span>
              <button type="button" class="tg-button tg-button--ghost" (click)="showMoreFiles()">
                <i class="fa-solid fa-chevron-down"></i>
                Show next {{ fileDisplayStep }}
              </button>
            </div>

            <div class="tg-empty" *ngIf="!loading && !visibleFiles.length">
              <i class="fa-regular fa-folder-open"></i>
              <strong>{{ files.length ? 'Try another filename.' : 'This folder is currently empty.' }}</strong>
              <span *ngIf="!files.length">Supported formats: TXT, CSV, DAT, JSON, XML, and LOG.</span>
            </div>
          </section>

          <section class="tg-panel tg-preview-panel">
            <header class="tg-preview-header">
              <div>
                <div class="tg-eyebrow">Safe file viewer</div>
                <h2>{{ previewFile?.filename || 'File preview' }}</h2>
                <p>{{ previewStatus }}</p>
              </div>

              <button
                type="button"
                class="tg-button tg-button--ghost"
                *ngIf="selectedFile"
                (click)="download(selectedFile)"
                [disabled]="!!downloadingFilename || !!previewingFilename"
              >
                <i class="fa-solid" [ngClass]="downloadingFilename ? 'fa-circle-notch fa-spin' : 'fa-download'"></i>
                <span>{{ downloadingFilename ? 'Downloading' : 'Download original' }}</span>
              </button>
            </header>

            <div class="tg-preview-meta" *ngIf="previewFile">
              <span><strong>Type</strong> {{ previewFile.extension | uppercase }}</span>
              <span><strong>File size</strong> {{ formatBytes(previewFile.size) }}</span>
              <span><strong>Preview</strong> {{ formatBytes(previewFile.content_bytes) }}</span>
              <span><strong>Modified</strong> {{ formatDate(previewFile.modified_at) }}</span>
            </div>

            <div class="tg-csv-wrap" *ngIf="isCsvPreview">
              <table class="tg-csv-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th *ngFor="let header of csvHeaders; trackBy: trackByIndex">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of csvRows; let rowIndex = index; trackBy: trackByIndex">
                    <td>{{ rowIndex + 1 }}</td>
                    <td *ngFor="let cell of row; trackBy: trackByIndex">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="tg-preview-footnote" *ngIf="csvRows.length >= previewRowLimit">
                Showing the first {{ previewRowLimit }} data rows.
              </div>
            </div>

            <pre class="tg-text-preview" *ngIf="previewFile && !isCsvPreview">{{ previewText }}</pre>

            <div class="tg-empty tg-empty--preview" *ngIf="!previewFile && !previewingFilename">
              <i class="fa-regular fa-file-lines"></i>
              <strong>Select a file to preview it.</strong>
              <span>CSV files open as a table. Fixed-width and other text files stay monospaced.</span>
            </div>

            <div class="tg-loading tg-loading--preview" *ngIf="previewingFilename">
              <i class="fa-solid fa-circle-notch fa-spin"></i>
              <strong>Loading {{ previewingFilename }}</strong>
            </div>
          </section>
        </section>
      </main>
    </div>
  </div>
</app-menu>
